import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
type ScreenId = 'home' | 'settle' | 'history';
const screens: {
  id: ScreenId;
  label: string;
  url: string;
  alt: string;
}[] = [{
  id: 'home',
  label: 'Home',
  url: 'https://cdn.magicpatterns.com/uploads/sTBFTcyBtGYPfv161wAixs/WhatsApp_Image_2026-08-13_at_19.06.02_(1).jpg',
  alt: 'KittyPot home screen for a Ski Trip kitty, showing total spent, the number of people sharing it, and controls to add an expense or receipt'
}, {
  id: 'settle',
  label: 'Settle',
  url: 'https://cdn.magicpatterns.com/uploads/uWNBZNF7MRi2yCCWeewHhk/WhatsApp_Image_2026-08-13_at_19.06.02.jpg',
  alt: 'KittyPot settlement screen showing what you are owed and the simplest set of payments to make everyone even'
}, {
  id: 'history',
  label: 'History',
  url: 'https://cdn.magicpatterns.com/uploads/mxhgmAEJufMAexcc2t4eXp/WhatsApp_Image_2026-08-13_at_19.06.02_(2).jpg',
  alt: 'KittyPot history screen listing recorded shared expenses with who paid and how much'
}];
const features = ['Everyone sees who owes what', 'Fair splits, equal or custom', 'The simplest way to settle up', 'Settle up your own way', 'Clear, private group expenses'];
export function ValueProposition() {
  const [activeScreen, setActiveScreen] = useState<ScreenId>('home');
  const prefersReducedMotion = useReducedMotion();
  const activeIndex = screens.findIndex(screen => screen.id === activeScreen);
  return <section className="py-20 lg:py-28 bg-kitty-yellow-50/50 overflow-hidden" data-id="element-270" data-mp="30yrh9399fp0g">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-271" data-mp="3ey0x21tk0h3w">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-272" data-mp="3exhi8tz7a8u3">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-273" data-mp="39yodbpxzbq5j">
            <p className="text-sm font-semibold text-kitty-yellow-600 uppercase tracking-widest mb-3" data-id="element-274" data-mp="1fmq5nsfbj2br">
              Full transparency
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-kitty-dark mb-6 leading-tight tracking-tight" data-id="element-275" data-mp="3klg0o1e5p33k">
              Keep every shared expense clear
            </h2>
            <p className="text-lg text-gray-500 mb-8 max-w-md" data-id="element-276" data-mp="3tnfigvwzwb7k">
              KittyPot brings every expense, balance and settlement update into
              one shared view, so your group always knows what is owed, pending
              and settled.
            </p>

            <ul className="space-y-4" data-id="element-277" data-mp="7dcy4hltzbpi">
              {features.map((feature, index) => <motion.li key={feature} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2 + index * 0.08
            }} className="flex items-center gap-3" data-id="element-278" data-mp="c6xyggawchrv" data-mpi={index}>
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-kitty-yellow/20 flex items-center justify-center" data-id="element-279" data-mp="2anzujrzipng4">
                    <CheckCircle2 className="w-4 h-4 text-kitty-yellow-700" data-id="element-280" data-mp="25ovhgq9h35ki" />
                  </div>
                  <span className="text-gray-800 font-medium text-base" data-id="element-281" data-mp="21ol3aqbj18uk">
                    {feature}
                  </span>
                </motion.li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative" data-id="element-282" data-mp="2tezci98lhwzw">
            <div className="relative z-10 mx-auto max-w-[390px]" data-id="element-283" data-mp="2fnchsduawwj8">
              <div className="relative overflow-hidden rounded-[2.25rem] border-[5px] border-kitty-dark bg-white shadow-[0_24px_52px_-24px_rgba(27,28,30,0.45)]" data-id="element-284" data-mp="2fn2pgjlsadad">
                <motion.div className="flex w-[300%]" animate={{
                x: `-${activeIndex * 100 / screens.length}%`
              }} transition={prefersReducedMotion ? {
                duration: 0
              } : {
                duration: 0.3,
                ease: [0.22, 1, 0.36, 1]
              }} data-id="element-285" data-mp="38aytk9don7h6">
                  {screens.map(screen => <img key={screen.id} src={screen.url} alt={screen.alt} aria-hidden={activeScreen !== screen.id} className="block h-auto w-1/3 flex-shrink-0" data-id="element-286" data-mp="2ixvttjh989ip" />)}
                </motion.div>

                {/* Tap targets over the app's bottom navigation */}
                <div role="tablist" aria-label="KittyPot app screens" className="absolute bottom-0 left-0 flex w-full h-[6.4%]" data-id="element-287" data-mp="3fl0grj2nnh6a">
                  {screens.map(screen => <button key={screen.id} type="button" role="tab" aria-selected={activeScreen === screen.id} aria-label={`Show ${screen.label} screen`} onClick={() => setActiveScreen(screen.id)} className="flex-1 cursor-pointer rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-288" data-mp="31su8ej5quehe" />)}
                </div>
              </div>

              <p className="mt-3 text-center text-xs font-medium text-gray-500" data-id="element-289" data-mp="ci5ldradiigi">
                The real KittyPot app — tap Home, Settle or History to explore.
              </p>
            </div>

            {/* Decorative blobs */}
            <div className="absolute top-10 -right-10 w-32 h-32 bg-kitty-yellow-200 rounded-full mix-blend-multiply filter blur-2xl opacity-60" data-id="element-290" data-mp="mb4e9lcwkv5x" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-orange-200 rounded-full mix-blend-multiply filter blur-2xl opacity-60" data-id="element-291" data-mp="3pr1p7wiw275b" />
          </motion.div>
        </div>
      </div>
    </section>;
}